// API client configuration
import i18n from '../i18n/config';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:13201/api/v1';
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 10000;

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  timeout?: number;
}

async function fetchWithTimeout(url: string, options: RequestOptions = {}): Promise<Response> {
  const { timeout = API_TIMEOUT, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Request timeout');
    }
    throw error;
  }
}

// T045 [US2]: Update API service to handle language parameter (include current lang in requests)
function buildUrl(url: string, includeLanguage: boolean = true): string {
  const fullUrl = `${API_BASE_URL}${url}`;

  if (!includeLanguage) {
    return fullUrl;
  }

  // Add language parameter to URL
  const currentLang = i18n.language;

  if (fullUrl.includes('lang=')) {
    return fullUrl;
  }

  const separator = fullUrl.includes('?') ? '&' : '?';
  return `${fullUrl}${separator}lang=${currentLang}`;
}

export async function get<T>(url: string, includeLanguage: boolean = true): Promise<T> {
  const response = await fetchWithTimeout(buildUrl(url, includeLanguage), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': i18n.language,
    },
  });

  if (!response.ok) {
    throw new ApiError(response.status, `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

export async function post<T>(url: string, data: unknown, includeLanguage: boolean = true): Promise<T> {
  const response = await fetchWithTimeout(buildUrl(url, includeLanguage), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': i18n.language,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new ApiError(response.status, `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

export { ApiError };
export type { RequestOptions };
